// src/components/modals/CreateLessonModal.jsx
import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SpinnerOverlay from '../ui/SpinnerOverlay';
import { useLessons } from '../../contexts/LessonsContext';
import { useAvailability } from '../../contexts/AvailabilityContext';
import { useUser } from '../../contexts/UserContext';

/* ── Module-level icon constants ─────────────────────────────── */
const IconCalendar = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
  </svg>
);
const IconClock = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
  </svg>
);
const IconDuration = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M7,13H17V11H7" />
  </svg>
);
const IconDocument = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);
const IconSms = (
  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12l-2-2-2 2-1.41-1.41L9.17 11 7.59 9.41 9 8l2 2 2-2 1.41 1.41L12.83 11l1.58 1.59L13 14z" />
  </svg>
);
const IconEmailNotif = (
  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const NOTIF_CONFIG = [
  {
    key: 'notifySms',
    isGreen: true,
    label: 'הודעות SMS',
    sub: 'שליחת הודעה לכל המשתתפים',
    icon: IconSms,
  },
  {
    key: 'notifyEmail',
    isGreen: false,
    label: 'הודעות אימייל',
    sub: 'שליחת הודעה מפורטת לכל המשתתפים',
    icon: IconEmailNotif,
  },
];

/* ── Shared helpers ──────────────────────────────────────────── */
const FieldLabel = ({ htmlFor, required, focused, hasValue, children }) => (
  <label
    htmlFor={htmlFor}
    className={`block text-xs font-bold uppercase tracking-wide mb-1.5 text-right transition-colors duration-150
                ${focused || hasValue ? 'text-indigo-500' : 'text-gray-500'}`}
  >
    {children}{' '}
    {required && (
      <span className="text-red-400 normal-case font-normal">*</span>
    )}
  </label>
);

const FieldError = ({ msg }) =>
  msg ? (
    <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center justify-end gap-1">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {msg}
    </p>
  ) : null;

/* ── InputField ──────────────────────────────────────────────── */
const InputField = forwardRef(function InputField(
  {
    label,
    name,
    type = 'text',
    placeholder,
    value,
    onChange,
    onBlur,
    error,
    required = false,
    icon,
    dir = 'rtl',
    ...props
  },
  ref
) {
  const [focused, setFocused] = useState(false);
  const isDate = type === 'date';

  return (
    <div className="min-w-0">
      <FieldLabel
        htmlFor={name}
        required={required}
        focused={focused}
        hasValue={!!value}
      >
        {label}
      </FieldLabel>
      <div className="relative flex items-center">
        {icon && (
          <span
            className={`absolute right-3 pointer-events-none transition-colors duration-150 ${
              focused ? 'text-indigo-400' : 'text-gray-400'
            }`}
          >
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={name}
          name={name}
          type={type}
          value={value}
          dir={dir}
          onChange={onChange}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          className={`block w-full min-w-0 h-10 ${
            icon ? 'pr-9' : 'pr-3'
          } pl-3 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                      outline-none transition-all duration-150 appearance-none
                      ${isDate ? '[color-scheme:light]' : ''}
                      ${
                        error
                          ? 'border-red-300 bg-red-50 ring-2 ring-red-100'
                          : focused
                          ? 'border-indigo-400 ring-2 ring-indigo-100'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
          {...props}
        />
      </div>
      <FieldError msg={error} />
    </div>
  );
});

/* ── SelectField ─────────────────────────────────────────────── */
function SelectField({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  required = false,
  icon,
  children,
  disabled = false,
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div className="min-w-0">
      <FieldLabel
        htmlFor={name}
        required={required}
        focused={focused}
        hasValue={!!value}
      >
        {label}
      </FieldLabel>
      <div className="relative flex items-center">
        {icon && (
          <span
            className={`absolute right-3 pointer-events-none z-10 transition-colors duration-150 ${
              focused ? 'text-indigo-400' : 'text-gray-400'
            }`}
          >
            {icon}
          </span>
        )}
        <select
          id={name}
          name={name}
          value={value}
          disabled={disabled}
          onChange={onChange}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          onFocus={() => setFocused(true)}
          className={`block w-full min-w-0 h-10 ${
            icon ? 'pr-9' : 'pr-3'
          } pl-7 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                      appearance-none outline-none transition-all duration-150
                      ${
                        error
                          ? 'border-red-300 bg-red-50'
                          : focused
                          ? 'border-indigo-400 ring-2 ring-indigo-100'
                          : 'border-gray-200 hover:border-indigo-300'
                      }
                      ${
                        disabled
                          ? 'opacity-50 cursor-not-allowed bg-gray-50'
                          : 'cursor-pointer'
                      }`}
        >
          {children}
        </select>
        <span className="absolute left-2.5 pointer-events-none text-gray-400">
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </div>
      <FieldError msg={error} />
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────── */
const pad2 = (n) => String(n).padStart(2, '0');

const toDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const [Y, M, D] = yyyyMmDd.split('-').map(Number);
  return new Date(Y, (M || 1) - 1, D || 1, 0, 0, 0, 0);
};

const addMinutesToTime = (hhmm, minutes) => {
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  d.setMinutes(d.getMinutes() + minutes);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const hmToMinutes = (hhmm) => {
  if (!hhmm) return 0;
  const [hStr, mStr] = String(hhmm).split(':');
  const h = parseInt(hStr || '0', 10);
  const m = parseInt(mStr || '0', 10);
  return isNaN(h) || isNaN(m) ? 0 : h * 60 + m;
};

const normHour = (hhmm) =>
  `${pad2(parseInt((hhmm || '00:00').split(':')[0] || '0', 10))}:00`;

/* ── Main component ──────────────────────────────────────────── */
export default function CreateLessonModal({
  isOpen,
  onClose,
  onSuccess,
  courseId,
  courseTitle,
  teacherId,
  defaultPlatform = 'ZOOM',
  slotMinutes = 50,
  mode = 'create',
  lesson = null,
}) {
  const { createGroupLesson, updateGroupLesson, getServerTime } = useLessons();
  const { showAlert } = useUser();
  const { timeSlots, getMonthlyAvailability } = useAvailability();

  const [isLoading, setIsLoading] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [maxHoursFromStart, setMaxHoursFromStart] = useState(0);
  const [serverNowMs, setServerNowMs] = useState(null);
  const [monthlyAvailabilityMap, setMonthlyAvailabilityMap] = useState({});

  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const getEffectiveNow = useCallback(() => {
    return new Date(Number.isFinite(serverNowMs) ? serverNowMs : Date.now());
  }, [serverNowMs]);

  const today = useMemo(() => {
    const d = getEffectiveNow();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }, [getEffectiveNow]);

  const isEditMode = mode === 'edit' && lesson;

  const schema = useMemo(
    () =>
      Yup.object({
        subject: Yup.string()
          .trim()
          .min(3, 'שם קצר מדי')
          .max(40, 'עד 40 תווים')
          .required('נושא חובה'),

        date: Yup.string()
          .required('תאריך חובה')
          .test('today-or-future', 'יש לבחור תאריך מהיום והלאה', (v) => {
            if (!v) return false;
            const d = toDate(v);
            if (!d) return false;

            const t0 = getEffectiveNow();
            t0.setHours(0, 0, 0, 0);

            return d >= t0;
          }),

        startTime: Yup.string()
          .required('שעה חובה')
          .matches(/^\d{2}:\d{2}$/, 'זמן לא תקין'),

        hours: Yup.number()
          .typeError('יש להזין מספר')
          .integer('מספר שלם')
          .min(1, 'לפחות שעה'),

        platform: Yup.string()
          .oneOf(['ZOOM', 'GOOGLE_MEET'])
          .required(),

        notifySms: Yup.boolean(),
        notifyEmail: Yup.boolean(),
      }),
    [getEffectiveNow]
  );

  const initialValues = useMemo(() => {
    if (isEditMode) {
      const start = (lesson.startTime || '').substring(0, 5);
      const end = (lesson.endTime || '').substring(0, 5);

      let hours = 1;
      if (start && end) {
        const diff = hmToMinutes(end) - hmToMinutes(start);
        if (diff > 0) {
          hours = Math.min(4, Math.max(1, Math.round(diff / slotMinutes)));
        }
      }

      return {
        subject: (lesson.title || '').trim(),
        date: lesson.date || today,
        startTime: start ? normHour(start) : '',
        hours,
        platform:
          lesson.platform === 'MEET' || lesson.platform === 'GOOGLE_MEET'
            ? 'GOOGLE_MEET'
            : 'ZOOM',
        notifySms: false,
        notifyEmail: false,
      };
    }

    return {
      subject: '',
      date: today,
      startTime: '',
      hours: 1,
      platform: defaultPlatform,
      notifySms: false,
      notifyEmail: false,
    };
  }, [isEditMode, lesson, today, defaultPlatform, slotMinutes]);

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, setFieldError, resetForm }) => {
      try {
        setIsLoading(true);

        if (!values.startTime) {
          setFieldError('startTime', 'בחר שעה זמינה');
          return;
        }

        if (maxHoursFromStart < 1) {
          setFieldError('startTime', 'השעה שנבחרה אינה זמינה');
          return;
        }

        if (values.hours > maxHoursFromStart) {
          setFieldError('hours', `ניתן לבחור עד ${maxHoursFromStart} שעות`);
          return;
        }

        const endTime = addMinutesToTime(
          values.startTime,
          values.hours * slotMinutes
        );

        const payload = {
          courseId,
          title: values.subject.trim() || 'מפגש חדש',
          date: values.date,
          startTime: values.startTime,
          endTime,
          platform: values.platform === 'GOOGLE_MEET' ? 'MEET' : 'ZOOM',
          notifySms: !!values.notifySms,
          notifyEmail: !!values.notifyEmail,
        };

        let result;
        if (isEditMode && lesson?.id) {
          result = await updateGroupLesson(lesson.id, payload);
          await refreshMonthlyAvailability(values.date);
          showAlert?.('המפגש עודכן בהצלחה!', 'success');
        } else {
          result = await createGroupLesson(payload);
          await refreshMonthlyAvailability(values.date);
          showAlert?.('המפגש נוצר בהצלחה!', 'success');
        }

        resetForm();
        onSuccess?.(result);
      } catch (e) {
        console.error(e);
        showAlert?.(
          e?.message ||
            (isEditMode ? 'שגיאה בעדכון המפגש' : 'שגיאה ביצירת המפגש'),
          'error'
        );
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  const { values } = formik;

  const slotIndexByStart = useCallback(
    (hhmm) =>
      (timeSlots || []).findIndex(
        (s) => normHour(s.start) === normHour(hhmm)
      ),
    [timeSlots]
  );

  const getLessonOwnedSlots = useCallback(
    (lessonDate, startTime, hours) => {
      if (!lessonDate || !startTime || !hours || !Array.isArray(timeSlots)) {
        return [];
      }

      const startIdx = slotIndexByStart(startTime);
      if (startIdx < 0) return [];

      const out = [];
      for (let i = 0; i < hours; i++) {
        const idx = startIdx + i;
        if (!timeSlots[idx]) break;
        out.push(normHour(timeSlots[idx].start));
      }

      return out;
    },
    [timeSlots, slotIndexByStart]
  );

  const computeAvailableTimesForDate = useCallback(
    (yyyyMmDd) => {
      if (!yyyyMmDd) return [];

      const d = toDate(yyyyMmDd);
      const now = getEffectiveNow();

      const isToday =
        d &&
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();

      const nowMin = hmToMinutes(
        `${pad2(now.getHours())}:${pad2(now.getMinutes())}`
      );
      const minAllowedStartMin = nowMin + 120;

      let out = Array.isArray(monthlyAvailabilityMap?.[yyyyMmDd])
        ? [...monthlyAvailabilityMap[yyyyMmDd]]
        : [];

      if (isToday) {
        out = out.filter(
          (startStr) => hmToMinutes(startStr) >= minAllowedStartMin
        );
      }

      return out.sort((a, b) => hmToMinutes(a) - hmToMinutes(b));
    },
    [monthlyAvailabilityMap, getEffectiveNow]
  );

  const computeMaxContiguous = useCallback(
    (yyyyMmDd, startTime) => {
      if (!yyyyMmDd || !startTime || !Array.isArray(timeSlots)) return 0;

      const available = computeAvailableTimesForDate(yyyyMmDd);
      if (!available.includes(startTime)) return 0;

      const startIdx = slotIndexByStart(startTime);
      if (startIdx < 0) return 0;

      let count = 0;

      for (let i = startIdx; i < timeSlots.length; i++) {
        const slotStart = normHour(timeSlots[i].start);
        if (available.includes(slotStart)) {
          count++;
        } else {
          break;
        }
      }

      return count;
    },
    [timeSlots, computeAvailableTimesForDate, slotIndexByStart]
  );

  const refreshMonthlyAvailability = useCallback(
    async (dateStr = values.date) => {
      if (!teacherId || !dateStr) return {};

      const d = toDate(dateStr);
      if (!d) return {};

      const year = d.getFullYear();
      const month = d.getMonth() + 1;

      const map = await getMonthlyAvailability(teacherId, year, month);
      const safeMap = map || {};
      setMonthlyAvailabilityMap(safeMap);
      return safeMap;
    },
    [teacherId, values.date, getMonthlyAvailability]
  );

  const editingInfo = useMemo(() => {
    if (!isEditMode || !lesson) return { date: null, start: null, hours: 1 };

    const start = (lesson.startTime || '').substring(0, 5);
    const end = (lesson.endTime || '').substring(0, 5);

    let hours = 1;
    if (start && end) {
      const diff = hmToMinutes(end) - hmToMinutes(start);
      if (diff > 0) {
        hours = Math.min(4, Math.max(1, Math.round(diff / slotMinutes)));
      }
    }

    return {
      date: lesson.date || null,
      start: start ? normHour(start) : null,
      hours,
    };
  }, [isEditMode, lesson, slotMinutes]);

  useEffect(() => {
    if (!isOpen) return;

    let list = computeAvailableTimesForDate(values.date);
    let editingStart = null;
    let editingOwnedSlots = [];

    if (editingInfo.date === values.date && editingInfo.start) {
      editingStart = editingInfo.start;
      editingOwnedSlots = getLessonOwnedSlots(
        editingInfo.date,
        editingInfo.start,
        editingInfo.hours
      );

      if (editingOwnedSlots.length) {
        list = Array.from(new Set([...list, ...editingOwnedSlots])).sort(
          (a, b) => hmToMinutes(a) - hmToMinutes(b)
        );
      }
    }

    setAvailableTimes(list);

    let startTime = values.startTime;

    if (!startTime && editingStart) {
      startTime = editingStart;
      formik.setFieldValue('startTime', editingStart, false);
      formik.setFieldTouched('startTime', false, false);
    }

    if (!startTime || !list.includes(startTime)) {
      if (!editingStart || startTime !== editingStart) {
        if (values.startTime !== '') {
          formik.setFieldValue('startTime', '', false);
        }
        if (values.hours !== 1) {
          formik.setFieldValue('hours', 1, false);
        }
        setMaxHoursFromStart(0);
        return;
      }
    }

    let maxH = computeMaxContiguous(values.date, startTime);

    if (
      isEditMode &&
      editingInfo.date === values.date &&
      editingInfo.start === startTime
    ) {
      const storedHours = editingInfo.hours || 1;
      if (maxH < storedHours) maxH = storedHours;
      if (values.hours !== storedHours) {
        formik.setFieldValue('hours', storedHours, false);
      }
    } else if (values.hours > maxH) {
      formik.setFieldValue('hours', Math.max(1, maxH), false);
    }

    setMaxHoursFromStart(maxH);
  }, [
    isOpen,
    values.date,
    values.startTime,
    values.hours,
    computeAvailableTimesForDate,
    computeMaxContiguous,
    editingInfo,
    isEditMode,
    monthlyAvailabilityMap,
    formik,
    getLessonOwnedSlots,
  ]);

  const firstInputRef = useRef(null);
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasAutoFocusedRef.current = false;
      return;
    }

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let t;
    const isSmallScreen = window.innerWidth < 640;

    if (!hasAutoFocusedRef.current && !isSmallScreen) {
      t = setTimeout(() => {
        firstInputRef.current?.focus();
        hasAutoFocusedRef.current = true;
      }, 50);
    }

    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prev;
      if (t) clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  useEffect(() => {
    let mounted = true;

    if (!isOpen) return;

    getServerTime()
      .then(async (ms) => {
        if (!mounted) return;
        setServerNowMs(ms);

        if (teacherId && values.date) {
          const d = toDate(values.date);
          if (d) {
            const year = d.getFullYear();
            const month = d.getMonth() + 1;
            const map = await getMonthlyAvailability(teacherId, year, month);
            if (!mounted) return;
            setMonthlyAvailabilityMap(map || {});
          }
        }
      })
      .catch((err) => {
        console.warn('[CreateLessonModal] failed to fetch server time:', err);
        if (!mounted) return;
        setServerNowMs(Date.now());
      });

    return () => {
      mounted = false;
    };
  }, [isOpen, getServerTime, teacherId, values.date, getMonthlyAvailability]);

  useEffect(() => {
    let mounted = true;

    if (!isOpen || !teacherId || !values.date) return;

    const d = toDate(values.date);
    if (!d) return;

    const year = d.getFullYear();
    const month = d.getMonth() + 1;

    getMonthlyAvailability(teacherId, year, month)
      .then((map) => {
        if (!mounted) return;
        setMonthlyAvailabilityMap(map || {});
      })
      .catch((err) => {
        console.error(
          '[CreateLessonModal] failed to load monthly availability:',
          err
        );
        if (!mounted) return;
        setMonthlyAvailabilityMap({});
      });

    return () => {
      mounted = false;
    };
  }, [isOpen, teacherId, values.date, getMonthlyAvailability]);

  if (!isOpen) return null;

  const showErrors = formik.submitCount > 0;
  const err = (k) => (showErrors ? formik.errors[k] : '');
  const isEdit = !!isEditMode;
  const close = () => onCloseRef.current?.();
  const submitLabel = isEdit ? 'עדכון מפגש' : 'יצירת מפגש';

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="create-lesson-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]"
        onClick={close}
      />

      {isLoading && (
        <SpinnerOverlay
          variant="glow"
          text="נא להמתין"
          dark={false}
          blur={false}
        />
      )}

      <div
        className="relative z-[1] w-full sm:max-w-2xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path
                  d={
                    isEdit
                      ? 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z'
                      : 'M12 5v14M5 12h14'
                  }
                />
              </svg>
            </div>
            <div className="min-w-0">
              <h2
                id="create-lesson-title"
                className="text-gray-800 font-bold text-base sm:text-lg leading-tight"
              >
                {isEdit ? 'עריכת מפגש' : 'יצירת מפגש חדש'}
              </h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">
                {isEdit
                  ? 'עדכון פרטי מפגש קיים'
                  : `הוספת מפגש לקורס ${courseTitle || ''}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="סגור"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form
          id="create-lesson-form"
          onSubmit={formik.handleSubmit}
          className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7 py-5"
        >
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InputField
                ref={firstInputRef}
                label="תאריך"
                name="date"
                type="date"
                dir="ltr"
                value={formik.values.date}
                min={today}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={err('date')}
                required
                icon={IconCalendar}
              />

              <SelectField
                label="שעת התחלה"
                name="startTime"
                value={values.startTime}
                onChange={(e) =>
                  formik.setFieldValue('startTime', e.target.value, false)
                }
                onBlur={() => {}}
                error={err('startTime')}
                required
                disabled={availableTimes.length === 0}
                icon={IconClock}
              >
                <option value="" disabled>
                  {availableTimes.length ? 'בחר שעה…' : 'אין שעות פנויות'}
                </option>
                {availableTimes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </SelectField>

              <SelectField
                label="משך (שעות)"
                name="hours"
                value={values.hours}
                onChange={(e) =>
                  formik.setFieldValue('hours', Number(e.target.value), false)
                }
                onBlur={() => {}}
                error={err('hours')}
                required
                disabled={!values.startTime || maxHoursFromStart < 1}
                icon={IconDuration}
              >
                {Array.from(
                  { length: Math.max(1, maxHoursFromStart) },
                  (_, i) => i + 1
                ).map((n) => (
                  <option key={n} value={n}>
                    {n} {n > 1 ? 'שעות' : 'שעה'}
                  </option>
                ))}
              </SelectField>
            </div>

            <InputField
              label="נושא המפגש"
              name="subject"
              type="text"
              placeholder="לדוגמה: מתמטיקה - משוואות ריבועיות"
              value={formik.values.subject}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={err('subject')}
              required
              icon={IconDocument}
            />

            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2.5 text-right">
                פלטפורמת המפגש{' '}
                <span className="text-red-400 normal-case font-normal">*</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    name="platform"
                    value="ZOOM"
                    checked={values.platform === 'ZOOM'}
                    onChange={() =>
                      formik.setFieldValue('platform', 'ZOOM', false)
                    }
                    className="sr-only"
                  />
                  <div
                    className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all duration-150
                                   ${
                                     values.platform === 'ZOOM'
                                       ? 'border-blue-400 bg-blue-50 shadow-sm shadow-blue-100'
                                       : 'border-gray-200 bg-white hover:border-gray-300'
                                   }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        values.platform === 'ZOOM'
                          ? 'bg-blue-500'
                          : 'bg-gray-300'
                      }`}
                    >
                      <svg
                        className="w-5 h-5 text-white"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`text-sm font-bold ${
                          values.platform === 'ZOOM'
                            ? 'text-blue-800'
                            : 'text-gray-600'
                        }`}
                      >
                        Zoom
                      </p>
                      <p className="text-[11px] text-gray-400">
                        פלטפורמה מובילה
                      </p>
                    </div>
                  </div>
                </label>

                <div className="opacity-40 cursor-not-allowed">
                  <div className="flex items-center gap-3 p-3.5 rounded-2xl border-2 border-gray-200 bg-gray-50">
                    <div className="w-9 h-9 rounded-xl bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-white"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.88 6.69L12 12.57 8.12 8.69 12 4.81l3.88 3.88zM12 19.19l-3.88-3.88L12 11.43l3.88 3.88L12 19.19z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-500">
                        Google Meet
                      </p>
                      <p className="text-[11px] text-gray-400">בקרוב</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2.5 text-right">
                הודעות והתראות
              </p>
              <div className="flex flex-col gap-2.5">
                {NOTIF_CONFIG.map(
                  ({ key, isGreen, label, sub, icon: notifIcon }) => (
                    <label key={key} className="cursor-pointer">
                      <div
                        className={`flex items-center justify-between gap-3 p-3.5 rounded-2xl border-2 transition-all duration-150
                                     ${
                                       values[key]
                                         ? isGreen
                                           ? 'border-emerald-300 bg-emerald-50'
                                           : 'border-blue-300 bg-blue-50'
                                         : 'border-gray-200 bg-white hover:border-gray-300'
                                     }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors
                                         ${
                                           values[key]
                                             ? isGreen
                                               ? 'bg-emerald-500'
                                               : 'bg-blue-500'
                                             : 'bg-gray-300'
                                         }`}
                          >
                            {notifIcon}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800">
                              {label}
                            </p>
                            <p className="text-[11px] text-gray-400 truncate">
                              {sub}
                            </p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={values[key]}
                          onChange={(e) =>
                            formik.setFieldValue(key, e.target.checked, false)
                          }
                          className={`w-4 h-4 flex-shrink-0 rounded border-gray-300
                                    ${
                                      isGreen
                                        ? 'text-emerald-500 focus:ring-emerald-400'
                                        : 'text-blue-500 focus:ring-blue-400'
                                    }`}
                        />
                      </div>
                    </label>
                  )
                )}
              </div>
            </div>
          </div>
        </form>

        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={close}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                       hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
          >
            ביטול
          </button>
          <button
            type="submit"
            form="create-lesson-form"
            disabled={formik.isSubmitting}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                       shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
                       transition-all duration-150 flex items-center gap-2"
          >
            {formik.isSubmitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {!formik.isSubmitting && (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d={isEdit ? 'M5 13l4 4L19 7' : 'M12 5v14M5 12h14'}
                />
              </svg>
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}